import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Ticket,
  FileText,
  BarChart3,
  Plug,
  ChevronsUpDown,
  AlertCircle,
  Zap,
  Sun,
  Moon,
  LogOut,
  X,
  Plus,
  Radio,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  MailWarning,
  UserPlus,
  Pin,
  PinOff,
  Layers,
  Menu,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { notificationRoute } from '@superchat/shared/notifications';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useWorkspace, ALL_APPS } from '../lib/workspace.jsx';
import { usePanels } from '../lib/panels.jsx';
import { initials } from '../lib/format.js';
import CreateAppDialog from './CreateAppDialog.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';

/**
 * `scope` is the grant each destination needs. A member without it gets no link
 * — the server would 403 the view anyway, and offering a door that does not
 * open is worse than not showing it.
 *
 * Team has no scope: any member may see who their colleagues are, and the view
 * hides its own editors unless the server says they can manage access.
 */
const NAV = [
  { to: '/chat', label: 'Chat', Icon: MessageCircle, counter: 'unread', scope: ['chat', 'read'] },
  { to: '/tickets', label: 'Tickets', Icon: Ticket, scope: ['chat', 'read'] },
  { to: '/docs', label: 'Docs', Icon: FileText, scope: ['docs', 'read'] },
  { to: '/analytics', label: 'Analytics', Icon: BarChart3, scope: ['analytics', 'read'] },
  { to: '/integration', label: 'Integration', Icon: Plug, scope: ['integration', 'read'] },
  { to: '/team', label: 'Team', Icon: Users },
];

/**
 * Which section the agent is in, for the mobile title bar. On a phone the
 * sidebar is behind a drawer, so the nav's own highlight — the usual answer to
 * "where am I" — is not on screen to be read.
 */
function visibleNavLabel(pathname) {
  return NAV.find(({ to }) => pathname.startsWith(to))?.label ?? 'Superchat';
}

/**
 * Nags an unverified account, and only when the server can actually deliver a
 * link. On a server with no Resend configuration, verification is not enforced
 * and this would be a banner asking for something impossible.
 */
function VerifyBanner() {
  const { user, updateUser } = useAuth();
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [state, setState] = useState('idle');

  useEffect(() => {
    api.config()
      .then((c) => setEmailEnabled(Boolean(c.emailEnabled)))
      .catch(() => setEmailEnabled(false));
  }, []);

  if (!user || user.emailVerified || !emailEnabled) return null;

  const resend = async () => {
    setState('sending');
    try {
      const result = await api.auth.resendVerification();
      if (result.alreadyVerified) {
        updateUser({ ...user, emailVerified: true });
        return;
      }
      setState(result.emailSent ? 'sent' : 'failed');
    } catch {
      setState('failed');
    }
  };

  return (
    <div className="verify-banner">
      <MailWarning size={14} />
      <span className="verify-banner__text">
        Confirm <strong>{user.email}</strong> to create apps and accept invitations. Everything
        else keeps working.
      </span>
      {state === 'sent' ? (
        <span className="verify-banner__done">Link sent — check your inbox.</span>
      ) : (
        <button type="button" className="ghost-button" onClick={resend} disabled={state === 'sending'}>
          {state === 'failed' ? 'Try again' : 'Resend link'}
        </button>
      )}
    </div>
  );
}

const PIN_KEY = 'superchat:appsPinned';

function AppSwitcher({ onCreate }) {
  const {
    apps,
    appIndex,
    activeApp,
    allApps,
    selectApp,
    loadingApps,
    appUnread,
    otherAppsUnread,
  } = useWorkspace();
  /**
   * Pinning keeps the list expanded.
   *
   * For agencies watching several tenants at once, where the list is not a
   * chooser you visit but a board you keep an eye on. Per browser rather than
   * per account: it is a property of the screen the agent is sitting at.
   * Unpinned, the menu behaves exactly as it always has.
   */
  const [pinned, setPinned] = useState(() => localStorage.getItem(PIN_KEY) === 'true');
  const [open, setOpen] = useState(pinned);
  const box = useRef(null);

  useEffect(() => {
    localStorage.setItem(PIN_KEY, String(pinned));
    // Pinning opens it; unpinning leaves it open until something closes it,
    // which is less startling than having the list vanish under the cursor.
    if (pinned) setOpen(true);
  }, [pinned]);

  useEffect(() => {
    if (!open || pinned) return undefined;
    const close = (event) => {
      if (!box.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open, pinned]);

  /* Choosing an app closes the menu — unless it is pinned, which is the point. */
  const chooseApp = (appId) => {
    selectApp(appId);
    if (!pinned) setOpen(false);
  };

  const current = activeApp ? appIndex[activeApp.id] : null;

  return (
    <div className="switcher" ref={box}>
      <button
        type="button"
        className="switcher__button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={allApps ? 'Every app, combined' : (activeApp?.name ?? 'Choose an app')}
      >
        <span
          className={`switcher__mark${allApps ? ' switcher__mark--all' : ''}`}
          style={allApps ? undefined : { background: current?.color ?? 'var(--line2)' }}
        >
          {allApps ? <Layers size={13} /> : (activeApp?.name ?? '?').charAt(0).toUpperCase()}
        </span>
        <span className="switcher__text">
          <span className="switcher__name">
            {allApps ? 'All apps' : (activeApp?.name ?? (loadingApps ? 'Loading…' : 'No app yet'))}
          </span>
          <span className="switcher__owner">
            {loadingApps
              ? ' '
              : apps.length === 0
                ? 'Create one to start'
                : allApps
                  ? `${apps.length} apps combined`
                  : `${apps.length} ${apps.length === 1 ? 'app' : 'apps'}`}
          </span>
        </span>
        {/* Something is waiting on an app that is not this one. Collapsed to a
            dot in the icon rail, where the label has nowhere to go. */}
        {otherAppsUnread > 0 && <span className="switcher__badge">{otherAppsUnread}</span>}
        <ChevronsUpDown size={13} className="switcher__chevron" />
      </button>

      {open && (
        <ul className={`switcher__menu${pinned ? ' is-pinned' : ''}`} role="listbox">
          <li className="switcher__pin">
            <button
              type="button"
              className={`switcher__pinbutton${pinned ? ' is-on' : ''}`}
              onClick={() => setPinned((v) => !v)}
              aria-pressed={pinned}
              title={pinned ? 'Let the list close again' : 'Keep this list open'}
            >
              {pinned ? <PinOff size={11} /> : <Pin size={11} />}
              {pinned ? 'Unpin list' : 'Keep open'}
            </button>
          </li>
          {/* One scope above the tenants rather than beside them: an agency
              watching six merchants wants the combined view first and the
              individual one only once something needs answering. Hidden with a
              single app, where "all" and "that one" are the same list. */}
          {apps.length > 1 && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={allApps}
                className={`switcher__item switcher__item--all${allApps ? ' is-active' : ''}`}
                onClick={() => chooseApp(ALL_APPS)}
                title="Chats, tickets and docs from every app in one list"
              >
                <span className="switcher__stripe switcher__stripe--all" />
                <Layers size={12} />
                <span className="switcher__name">All apps</span>
                {otherAppsUnread > 0 && !allApps ? (
                  <span className="switcher__badge">{otherAppsUnread}</span>
                ) : (
                  <span className="switcher__slug">{apps.length} combined</span>
                )}
              </button>
            </li>
          )}
          {apps.map((app) => (
            <li key={app.id}>
              <button
                type="button"
                role="option"
                aria-selected={app.id === activeApp?.id}
                className={`switcher__item${app.id === activeApp?.id ? ' is-active' : ''}`}
                onClick={() => chooseApp(app.id)}
              >
                <span
                  className="switcher__stripe"
                  style={{ background: appIndex[app.id]?.color }}
                />
                <span className="switcher__name">{app.name}</span>
                {appUnread[app.id] > 0 ? (
                  <span className="switcher__badge">{appUnread[app.id]}</span>
                ) : (
                  <span className="switcher__slug">{app.slug}</span>
                )}
              </button>
            </li>
          ))}
          {apps.length === 0 && <li className="switcher__empty">No apps yet</li>}
          <li>
            <button
              type="button"
              className="switcher__item switcher__item--new"
              onClick={() => {
                if (!pinned) setOpen(false);
                onCreate();
              }}
            >
              <Plus size={12} />
              New app
            </button>
          </li>
        </ul>
      )}

      {/* Tenant strip: which app of the agency's portfolio is in focus — every
          one of them, lit, when the scope is all of them. */}
      <div className="switcher__dots">
        {apps.map((app) => (
          <span
            key={app.id}
            className="switcher__dot"
            style={{
              background:
                allApps || app.id === activeApp?.id ? appIndex[app.id]?.color : 'var(--line2)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') ?? 'dark'
  );

  const flip = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('superchat:theme', next);
    setTheme(next);
  };

  return (
    <button type="button" className="icon-button" onClick={flip} title="Toggle theme">
      {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
    </button>
  );
}

export function Shell() {
  const { user, logout } = useAuth();
  const {
    unreadCount,
    emergencies,
    connection,
    toast,
    dismissToast,
    apps,
    loadingApps,
    can,
  } = useWorkspace();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { compact, mobile, nav: navOpen, toggle, close } = usePanels();
  const [creating, setCreating] = useState(false);

  const noApps = !loadingApps && apps.length === 0;

  /**
   * On a phone the menu is a drawer over the view, so following a link inside
   * it has to put it away — otherwise the destination arrives underneath a
   * sheet the agent then has to dismiss by hand. Above the breakpoint the
   * sidebar is a column that navigating does not disturb, so this does nothing.
   */
  useEffect(() => {
    if (mobile && navOpen) close('nav');
    // The pathname is the event: a navigation happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const activeNav = visibleNavLabel(pathname);

  // `can` answers true while the app list is still loading: showing every link
  // and then removing some reads as a glitch, so the nav waits for the answer.
  // In the all-apps scope it is the union — a link is worth offering if any
  // tenant in scope would honour it.
  const visibleNav = NAV.filter(({ scope }) => !scope || can(...scope));

  return (
    <div className={`shell${compact ? ' shell--compact' : ''}`}>
      {/* Tapping away from a drawer closes it — the gesture everyone already
          has for "I am done with this". Rendered only while one is open, so it
          never sits invisibly over the view swallowing taps. */}
      {mobile && navOpen && (
        <button
          type="button"
          className="scrim"
          aria-label="Close menu"
          onClick={() => close('nav')}
        />
      )}

      <aside
        className={`sidebar${navOpen ? '' : ' is-collapsed'}${
          mobile && navOpen ? ' is-drawer-open' : ''
        }`}
      >
        <div className="sidebar__top">
          <div className="brand">
            <Zap size={15} className="brand__bolt" />
            <span className="brand__name">Superchat</span>
            <span className="brand__tag">AGENCY</span>
            <button
              type="button"
              className="icon-button brand__toggle"
              onClick={() => toggle('nav')}
              aria-expanded={navOpen}
              aria-label={navOpen ? 'Collapse menu' : 'Expand menu'}
              title={
                navOpen
                  ? 'Collapse menu'
                  : compact
                    ? 'Expand menu — this closes the context panel'
                    : 'Expand menu'
              }
            >
              {navOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
            </button>
          </div>
          <AppSwitcher onCreate={() => setCreating(true)} />
        </div>

        <nav className="nav">
          <div className="nav__label">WORKSPACE</div>
          {visibleNav.map(({ to, label, Icon, counter }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav__item${isActive ? ' is-active' : ''}`}
              title={label}
            >
              <span className="nav__icon">
                <Icon size={15} />
              </span>
              <span className="nav__text">{label}</span>
              {counter === 'unread' && unreadCount > 0 && (
                <span className="nav__badge">{unreadCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {emergencies.length > 0 && (
          <button
            type="button"
            className="emergency-card"
            onClick={() => navigate('/chat')}
            title={`${emergencies.length} active emergenc${
              emergencies.length === 1 ? 'y' : 'ies'
            }`}
          >
            <div className="emergency-card__head">
              <AlertCircle size={12} />
              <span className="emergency-card__count">
                {emergencies.length} ACTIVE EMERGENC{emergencies.length === 1 ? 'Y' : 'IES'}
              </span>
            </div>
            <div className="emergency-card__body">
              {emergencies
                .slice(0, 2)
                .map((c) => c.visitor.name || c.visitor.visitorId)
                .join(' · ')}
            </div>
          </button>
        )}

        <div className="sidebar__foot">
          <div
            className="presence"
            title={
              connection === 'connected'
                ? 'Accepting chats'
                : connection === 'idle'
                  ? 'No app selected'
                  : 'Reconnecting…'
            }
          >
            <span
              className="presence__dot"
              style={{
                background:
                  connection === 'connected'
                    ? 'var(--ok)'
                    : connection === 'idle'
                      ? 'var(--tx3)'
                      : 'var(--warn)',
              }}
            />
            <span className="presence__label">
              {connection === 'connected'
                ? 'Accepting chats'
                : connection === 'idle'
                  ? 'No app selected'
                  : 'Reconnecting…'}
            </span>
          </div>

          <div className="me">
            <span className="me__avatar" title={user?.name || user?.email}>
              {initials(user?.name || user?.email)}
            </span>
            <span className="me__text">
              <span className="me__name">{user?.name || user?.email}</span>
              <span className="me__role">{user?.role}</span>
            </span>
            <ThemeToggle />
            <button
              type="button"
              className="icon-button"
              onClick={logout}
              title="Log out everywhere"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        {/* The phone's own chrome: the way back to the menu, where you are, and
            the two controls the sidebar foot would otherwise be hiding. Purely
            CSS-hidden above the breakpoint — one bar, not a second layout. */}
        <div className="topbar">
          <button
            type="button"
            className="icon-button topbar__menu"
            onClick={() => toggle('nav')}
            aria-expanded={navOpen}
            aria-label="Open menu"
          >
            <Menu size={16} />
          </button>
          <Zap size={14} className="brand__bolt" />
          <span className="topbar__title">{activeNav}</span>
          {unreadCount > 0 && <span className="nav__badge">{unreadCount}</span>}
          <div className="topbar__actions">
            <ThemeToggle />
          </div>
        </div>

        <VerifyBanner />
        <div className="main__inner">
          {noApps ? (
            <div className="onboard">
              <h1 className="onboard__title">No apps yet</h1>
              <p className="onboard__body">
                Every conversation, ticket and doc lives under an app. Create one to get a public
                key and an install snippet — or sign in as the seeded owner account if you ran
                <code> npm run seed</code>, which creates four demo tenants.
              </p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setCreating(true)}
              >
                <Plus size={13} /> Create your first app
              </button>
            </div>
          ) : (
            /* Keyed by route so the boundary resets when the agent navigates:
               a view that threw must not keep the next one from mounting.
               Scoped here rather than only at the root so the sidebar survives
               and there is still somewhere to click. */
            <ErrorBoundary key={pathname} scope={pathname.replace('/', '')}>
              <Outlet />
            </ErrorBoundary>
          )}
        </div>
      </main>

      {creating && <CreateAppDialog onClose={() => setCreating(false)} />}

      {/* An emergency from a merchant arrives with sound; this is the visual
          half, and it survives whichever view the agent is on. */}
      {toast && (
        <div className={`toast toast--${toast.kind}`} role="alert">
          {toast.kind === 'ping' ? (
            <Radio size={15} />
          ) : toast.kind === 'assignment' ? (
            <UserPlus size={15} />
          ) : toast.kind === 'message' ? (
            <MessageCircle size={15} />
          ) : (
            <AlertCircle size={15} />
          )}
          <div className="toast__text">
            <div className="toast__title">{toast.title}</div>
            <div className="toast__body">{toast.body}</div>
          </div>
          <button
            type="button"
            className="ghost-button"
            onClick={() => {
              dismissToast();
              /**
               * The same routing every other notification uses.
               *
               * A toast carries its own appId, because it may be about a tenant
               * that is not in focus — ChatView switches app before resolving
               * the conversation, so one click lands on the right thread rather
               * than an empty list.
               */
              navigate(
                notificationRoute({
                  appId: toast.appId,
                  conversationId: toast.conversationId,
                  ticketId: toast.ticketId,
                  kind: toast.what,
                })
              );
            }}
          >
            Open
          </button>
          <button type="button" className="icon-button" onClick={dismissToast} aria-label="Dismiss">
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

export default Shell;
